/**
 * AdminImageNamingCard — Admin UI for the SEO image-renaming scheme.
 *
 * Every image uploaded across the platform is renamed using a cycle pattern
 * that repeats AAABBBCCCDDDEF (14 slots). This card lets admin:
 *   - toggle the scheme on/off
 *   - rename the prefixes ("top" / "best")
 *   - rename the wedding / birthday labels
 *   - manage the rotating list of "other events" used in slots E + F
 *   - override the slug used for any role:category vendor type
 *   - run a one-shot backfill that renames every existing image on disk
 *
 * The right-hand pane previews the resolved labels for the upcoming 14
 * filenames so admin can confirm the scheme before saving.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Tag, FloppyDisk, Spinner, ArrowsClockwise, Plus, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminImageNamingCard = () => {
  const [settings, setSettings] = useState(null);
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  // Editable form state — synced from `settings` on load.
  const [enabled, setEnabled] = useState(true);
  const [prefixTop, setPrefixTop] = useState('top');
  const [prefixBest, setPrefixBest] = useState('best');
  const [weddingLabel, setWeddingLabel] = useState('wedding');
  const [birthdayLabel, setBirthdayLabel] = useState('birthday');
  const [defaultCity, setDefaultCity] = useState('india');
  const [otherEvents, setOtherEvents] = useState([]);
  const [newEvent, setNewEvent] = useState('');
  const [overrides, setOverrides] = useState([]); // [{key, label}]
  const [newOverrideKey, setNewOverrideKey] = useState('');
  const [newOverrideLabel, setNewOverrideLabel] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/image-naming`, { withCredentials: true });
      setSettings(data.settings || {});
      setPreview(data.cycle_preview || []);
      const s = data.settings || {};
      setEnabled(s.enabled !== false);
      setPrefixTop(s.prefix_top || 'top');
      setPrefixBest(s.prefix_best || 'best');
      setWeddingLabel(s.wedding_label || 'wedding');
      setBirthdayLabel(s.birthday_label || 'birthday');
      setDefaultCity(s.default_city || 'india');
      setOtherEvents(Array.isArray(s.other_events) ? s.other_events : []);
      const m = s.vendor_type_overrides || {};
      setOverrides(Object.entries(m).map(([key, label]) => ({ key, label })));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load image-naming settings');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // Show "AAA BBB CCC DDD E F" hint alongside the live preview.
  const cycleLetters = useMemo(() => 'AAABBBCCCDDDEF'.split(''), []);

  const addOtherEvent = () => {
    const v = (newEvent || '').trim();
    if (!v) return;
    if (otherEvents.includes(v)) {
      toast.warning('Already in the list');
      return;
    }
    setOtherEvents((cur) => [...cur, v]);
    setNewEvent('');
  };

  const removeOtherEvent = (idx) => {
    setOtherEvents((cur) => cur.filter((_, i) => i !== idx));
  };

  const addOverride = () => {
    const k = (newOverrideKey || '').trim().toLowerCase();
    const l = (newOverrideLabel || '').trim();
    if (!k || !l) return;
    if (overrides.some((o) => o.key === k)) {
      toast.warning(`Override for "${k}" already exists`);
      return;
    }
    setOverrides((cur) => [...cur, { key: k, label: l }]);
    setNewOverrideKey('');
    setNewOverrideLabel('');
  };

  const removeOverride = (idx) => {
    setOverrides((cur) => cur.filter((_, i) => i !== idx));
  };

  const save = async () => {
    setSaving(true);
    try {
      const vto = {};
      overrides.forEach(({ key, label }) => {
        if (key && label) vto[key] = label;
      });
      const payload = {
        enabled,
        prefix_top: prefixTop,
        prefix_best: prefixBest,
        wedding_label: weddingLabel,
        birthday_label: birthdayLabel,
        default_city: defaultCity,
        other_events: otherEvents,
        vendor_type_overrides: vto,
      };
      const { data } = await axios.put(`${BACKEND_URL}/api/admin/image-naming`, payload, { withCredentials: true });
      setSettings(data.settings || {});
      setPreview(data.cycle_preview || []);
      toast.success('Image naming saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const runBackfill = async () => {
    if (!window.confirm(
      'Rename every existing portfolio / decor / gallery image on disk using the SEO scheme?\n\n' +
      'This is one-shot and idempotent (files already in SEO format are skipped). ' +
      'It updates database URLs in place — existing customer-facing links will continue to work.'
    )) return;
    setBackfilling(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/image-naming/backfill`, {}, { withCredentials: true });
      toast.success(
        `Backfill done — ${data.files_renamed} renamed, ${data.files_already_seo} already-SEO, ` +
        `${data.files_missing_on_disk} missing on disk (${data.users_scanned} users scanned)`,
        { duration: 6000 }
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Backfill failed');
    }
    setBackfilling(false);
  };

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="admin-image-naming-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <Tag size={20} className="text-brand-primary" weight="fill" /> SEO Image Renaming
        </CardTitle>
        <CardDescription>
          Every image uploaded across the platform is renamed using the cycle{' '}
          <span className="font-mono font-semibold">AAA&nbsp;BBB&nbsp;CCC&nbsp;DDD&nbsp;E&nbsp;F</span> (14 slots, repeats per vendor).
          Pattern: <span className="font-mono">{`<prefix>-<vendor-type>-<event>-<city>-<seq>-<id>.<ext>`}</span> —
          for example <span className="font-mono">top-photographer-wedding-mumbai-1-a3f9b1.jpg</span>.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-text-secondary py-4 text-center">Loading…</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {/* LEFT — editable fields */}
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 border border-[#E8E1D5]">
                <div>
                  <div className="text-sm font-medium">Renaming enabled</div>
                  <div className="text-[11px] text-text-secondary">
                    When off, uploads keep their random-hash names (no SEO benefit).
                  </div>
                </div>
                <Switch checked={enabled} onCheckedChange={setEnabled}
                  data-testid="admin-naming-enabled-switch" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-bold text-text-secondary">Prefix “top” →</label>
                  <Input value={prefixTop} onChange={(e) => setPrefixTop(e.target.value)}
                    className="h-9 rounded-none border-[#E8E1D5] mt-1" data-testid="admin-naming-prefix-top" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-bold text-text-secondary">Prefix “best” →</label>
                  <Input value={prefixBest} onChange={(e) => setPrefixBest(e.target.value)}
                    className="h-9 rounded-none border-[#E8E1D5] mt-1" data-testid="admin-naming-prefix-best" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-bold text-text-secondary">Wedding label</label>
                  <Input value={weddingLabel} onChange={(e) => setWeddingLabel(e.target.value)}
                    className="h-9 rounded-none border-[#E8E1D5] mt-1" data-testid="admin-naming-wedding-label" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-bold text-text-secondary">Birthday label</label>
                  <Input value={birthdayLabel} onChange={(e) => setBirthdayLabel(e.target.value)}
                    className="h-9 rounded-none border-[#E8E1D5] mt-1" data-testid="admin-naming-birthday-label" />
                </div>
                <div className="col-span-2">
                  <label className="text-[11px] uppercase tracking-wider font-bold text-text-secondary">Fallback city (when vendor hasn’t set one)</label>
                  <Input value={defaultCity} onChange={(e) => setDefaultCity(e.target.value)}
                    className="h-9 rounded-none border-[#E8E1D5] mt-1" data-testid="admin-naming-default-city" />
                </div>
              </div>

              {/* Other events — round-robin for slots E + F */}
              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-text-secondary mb-1">
                  Other-event labels (used in slot E + F, round-robin per cycle)
                </div>
                <div className="space-y-1.5 mb-2">
                  {otherEvents.length === 0 && (
                    <p className="text-xs text-text-secondary italic">Add at least one to make E/F slots meaningful.</p>
                  )}
                  {otherEvents.map((ev, i) => (
                    <div key={i} className="flex items-center gap-2 px-2 py-1 border border-[#E8E1D5] bg-sand-50"
                      data-testid={`admin-naming-other-event-${i}`}>
                      <span className="text-sm font-mono flex-1">{ev}</span>
                      <button onClick={() => removeOtherEvent(i)}
                        className="text-brand-primary hover:text-[#7B2E22]"
                        data-testid={`admin-naming-other-event-remove-${i}`}>
                        <Trash size={14} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={newEvent} onChange={(e) => setNewEvent(e.target.value)}
                    placeholder="e.g. anniversary"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOtherEvent(); } }}
                    className="h-9 rounded-none border-[#E8E1D5] flex-1"
                    data-testid="admin-naming-other-event-input" />
                  <Button onClick={addOtherEvent} variant="outline"
                    className="rounded-none border-[#E8E1D5] h-9"
                    data-testid="admin-naming-other-event-add">
                    <Plus size={14} className="mr-1" /> Add
                  </Button>
                </div>
              </div>

              {/* Vendor-type overrides */}
              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-text-secondary mb-1">
                  Vendor-type slug overrides
                </div>
                <p className="text-[11px] text-text-secondary mb-2">
                  Key = <span className="font-mono">role:category</span> (or just <span className="font-mono">category</span>) — value becomes the slug.
                  Example: <span className="font-mono">vendor:catering</span> → <span className="font-mono">caterer-mumbai</span>.
                </p>
                <div className="space-y-1.5 mb-2">
                  {overrides.length === 0 && (
                    <p className="text-xs text-text-secondary italic">No overrides — slugs are derived from category/role.</p>
                  )}
                  {overrides.map((o, i) => (
                    <div key={i} className="flex items-center gap-2 px-2 py-1 border border-[#E8E1D5] bg-sand-50"
                      data-testid={`admin-naming-override-${i}`}>
                      <span className="text-sm font-mono flex-1">{o.key}</span>
                      <span className="text-text-secondary text-xs">→</span>
                      <span className="text-sm font-mono flex-1">{o.label}</span>
                      <button onClick={() => removeOverride(i)}
                        className="text-brand-primary hover:text-[#7B2E22]"
                        data-testid={`admin-naming-override-remove-${i}`}>
                        <Trash size={14} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-5 gap-2">
                  <Input value={newOverrideKey} onChange={(e) => setNewOverrideKey(e.target.value)}
                    placeholder="vendor:catering"
                    className="h-9 rounded-none border-[#E8E1D5] col-span-2"
                    data-testid="admin-naming-override-key" />
                  <Input value={newOverrideLabel} onChange={(e) => setNewOverrideLabel(e.target.value)}
                    placeholder="caterer"
                    className="h-9 rounded-none border-[#E8E1D5] col-span-2"
                    data-testid="admin-naming-override-label" />
                  <Button onClick={addOverride} variant="outline"
                    className="rounded-none border-[#E8E1D5] h-9"
                    data-testid="admin-naming-override-add">
                    <Plus size={14} />
                  </Button>
                </div>
              </div>
            </div>

            {/* RIGHT — live preview + actions */}
            <div className="space-y-4">
              <div className="border border-[#E8E1D5] bg-sand-50 p-3">
                <div className="text-[11px] uppercase tracking-wider font-bold text-text-secondary mb-2">
                  Cycle preview (next 14 uploads, repeats)
                </div>
                <div className="space-y-1 font-mono text-[11px]">
                  {(preview.length ? preview : cycleLetters.map((l, i) => ({ slot_index: i, letter: l, prefix: '?', event: '?' }))).map((p) => (
                    <div key={p.slot_index} className="flex items-center gap-2"
                      data-testid={`admin-naming-preview-${p.slot_index}`}>
                      <span className="text-text-secondary w-6">{String(p.slot_index + 1).padStart(2, ' ')}</span>
                      <span className={`w-5 font-bold ${
                        p.letter === 'A' ? 'text-brand-primary' :
                        p.letter === 'B' ? 'text-[#9C5520]' :
                        p.letter === 'C' ? 'text-emerald-700' :
                        p.letter === 'D' ? 'text-blue-700' :
                        p.letter === 'E' ? 'text-purple-700' : 'text-rose-700'
                      }`}>{p.letter}</span>
                      <span className="text-text-secondary">
                        {p.prefix}-&lt;vendor&gt;-<span className="text-text-primary font-semibold">{p.event}</span>-&lt;city&gt;
                      </span>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] text-text-secondary mt-2">
                  After slot 14 the sequence resets — but the “other-event” label round-robins to the next pair in your list.
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Button onClick={save} disabled={saving}
                  className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50 disabled:opacity-60 h-10"
                  data-testid="admin-naming-save-btn">
                  {saving ? <Spinner size={14} className="mr-2 animate-spin" /> : <FloppyDisk size={14} className="mr-2" weight="fill" />}
                  Save settings
                </Button>
                <Button onClick={runBackfill} disabled={backfilling} variant="outline"
                  className="rounded-none border-[#E8E1D5] h-10"
                  data-testid="admin-naming-backfill-btn">
                  {backfilling ? <Spinner size={14} className="mr-2 animate-spin" /> : <ArrowsClockwise size={14} className="mr-2" weight="bold" />}
                  Backfill existing images
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminImageNamingCard;
